import React, { useState, useEffect } from 'react';
import api from '../services/apiService';
import { Helmet } from 'react-helmet';
import Select from 'react-select';

function formatDate(dateString) {
  if (!dateString) return "-";
  const d = new Date(dateString);
  if (isNaN(d)) return "-";
  return `${String(d.getDate()).padStart(2, '0')}.${String(d.getMonth() + 1).padStart(2, '0')}.${d.getFullYear()}`;
}

function extractAssets(data) {
  // Falls das Backend { data: [...] } liefert, nimm data.data, sonst nimm data selbst (sollte dann ein Array sein)
  return Array.isArray(data?.data) ? data.data : data;
}

function normalizeItemSearch(value) {
  return String(value || '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLocaleLowerCase('de')
    .trim();
}

function filterItemOption({ data }, inputValue) {
  const searchTerm = normalizeItemSearch(inputValue);
  if (!searchTerm) return true;

  return normalizeItemSearch(`${data.label} ${data.description || ''}`)
    .includes(searchTerm);
}

function formatItemOption(option, { context }) {
  if (context !== 'menu' || !option.description) return option.label;

  return (
    <div>
      <div>{option.label}</div>
      <div style={{
        color: '#666',
        fontSize: 12,
        lineHeight: 1.3,
        whiteSpace: 'normal',
        display: '-webkit-box',
        WebkitLineClamp: 2,
        WebkitBoxOrient: 'vertical',
        overflow: 'hidden',
      }}>
        {option.description}
      </div>
    </div>
  );
}


const AssetForm = ({
  pipelineInstId,
  instanceCustomerId,
  instanceContactId,
  instanceProspectId,
  instanceProspectContactId,
  showFilters = true,
  showAddRow = false,
}) => {
  const [assets, setAssets] = useState([]);
  const [items, setItems] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [prospects, setProspects] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const limit = 25; // oder was du willst
  const [newAsset, setNewAsset] = useState({
    pipelineInstId: pipelineInstId || '',
    customerId: instanceCustomerId || '',
    contactId: instanceContactId || '',
    prospectId: instanceProspectId || '',
    prospectContactId: instanceProspectContactId || '',
    itemId: '',
    customDescription: '',
    customType: '',
    quantity: 1,
    periodFrom: '',
    periodTo: '',
    status: 'Mietanfrage'
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [editingAsset, setEditingAsset] = useState(null);

  const [filter, setFilter] = useState({
  customerId: '',
  prospectId: '',
  pipelineInstId: '',
  itemId: '',
  dateFrom: '',
  dateTo: '',
  description: '',
});

  // Assets und Stammdaten laden
useEffect(() => {
  const fetchAll = async () => {
    try {
      // Filter- und Pagination-Parameter bauen:
      const params = {
        customerId: filter.customerId,
        prospectId: filter.prospectId,
        pipelineInstId: pipelineInstId || filter.pipelineInstId,
        itemId: filter.itemId,
        dateFrom: filter.dateFrom,
        dateTo: filter.dateTo,
        description: filter.description,
        limit,
        page: currentPage,
      };
      // Leere Felder rauswerfen:
      Object.keys(params).forEach(k => (params[k] === '' || params[k] == null) && delete params[k]);

      const [asRes, itemsRes, customersRes, prospectsRes] = await Promise.all([
        api.get('/assets', { params }),
        api.get('/items'),
        api.get('/customers'),
        api.get('/prospects', { params: { limit: 200 } }),
      ]);
      setAssets(extractAssets(asRes.data));
      setItems(itemsRes.data);
      setCustomers(customersRes.data);
      setProspects(prospectsRes.data.rows || []);

      setTotalPages(asRes.data.pages || 1); // <-- Pagination!
      // Kontakte für den aktuellen Kunden laden
      if (instanceCustomerId) {
        const contactsRes = await api.get(`/contacts/customer/${instanceCustomerId}`);
        setContacts(contactsRes.data);
      } else if (instanceProspectId) {
        const contactsRes = await api.get(`/prospects/${instanceProspectId}/contacts`);
        setContacts(contactsRes.data);
      } else {
        setContacts([]);
      }
    } catch (err) {
      setError('Fehler beim Laden der Assets.');
    }
    setLoading(false);
  };
  fetchAll();
  // NEU: auch auf Filter und Pagination hören!
}, [
  pipelineInstId,
  instanceCustomerId,
  instanceProspectId,
  filter.customerId,
  filter.prospectId,
  filter.pipelineInstId,
  filter.itemId,
  filter.dateFrom,
  filter.dateTo,
  filter.description,
  currentPage,
]);



  // Beim Editieren die Ansprechpartner des ausgewählten Unternehmens laden.
  useEffect(() => {
    if (editingAsset?.customerId) {
      api.get(`/contacts/customer/${editingAsset.customerId}`)
        .then(res => setContacts(res.data))
        .catch(() => setContacts([]));
      return;
    }
    if (editingAsset?.prospectId) {
      api.get(`/prospects/${editingAsset.prospectId}/contacts`)
        .then(res => setContacts(res.data))
        .catch(() => setContacts([]));
      return;
    }
    setContacts([]);
  }, [editingAsset?.customerId, editingAsset?.prospectId]);

  const handleEditAccountChange = option => {
    setContacts([]);
    if (!option) {
      setEditingAsset(prev => ({
        ...prev,
        customerId: null,
        contactId: null,
        prospectId: null,
        prospectContactId: null,
      }));
      return;
    }

    if (option.accountType === 'prospect') {
      setEditingAsset(prev => ({
        ...prev,
        customerId: null,
        contactId: null,
        prospectId: option.accountId,
        prospectContactId: null,
      }));
      return;
    }

    setEditingAsset(prev => ({
      ...prev,
      customerId: option.accountId,
      contactId: null,
      prospectId: null,
      prospectContactId: null,
    }));
  };

  const handleEditContactChange = option => {
    setEditingAsset(prev => (
      prev.prospectId
        ? { ...prev, prospectContactId: option?.value || null }
        : { ...prev, contactId: option?.value || null }
    ));
  };

  // Hinzufügen
  const handleAddAsset = async () => {
    if (!newAsset.itemId || !newAsset.quantity || !pipelineInstId || (!instanceCustomerId && !instanceProspectId)) {
      alert('Bitte alle Pflichtfelder ausfüllen (Artikel, Menge).');
      return;
    }
    try {
      const payload = {
        pipelineInstId,
        customerId: instanceCustomerId || null,
        contactId: instanceProspectId
          ? null
          : (instanceContactId || (contacts.length > 0 ? contacts[0].id : null)),
        prospectId: instanceProspectId || null,
        prospectContactId: instanceProspectContactId || (instanceProspectId && contacts.length > 0 ? contacts[0].id : null),
        itemId: newAsset.itemId,
        customDescription: newAsset.customDescription,
        customType: newAsset.customType,
        quantity: newAsset.quantity,
        periodFrom: newAsset.periodFrom,
        periodTo: newAsset.periodTo,
        status: newAsset.status || 'Mietanfrage',
      };
      await api.post('/assets', payload);
      const asRes = await api.get('/assets', { params: { pipelineInstId } });
      setAssets(extractAssets(asRes.data));
      setNewAsset({
        pipelineInstId: pipelineInstId || '',
        customerId: instanceCustomerId || '',
        contactId: instanceContactId || '',
        prospectId: instanceProspectId || '',
        prospectContactId: instanceProspectContactId || '',
        itemId: '',
        customDescription: '',
        customType: '',
        quantity: 1,
        periodFrom: '',
        periodTo: '',
        status: 'Mietanfrage'
      });
    } catch (err) {
      alert(err.response?.data?.error || 'Fehler beim Hinzufügen des Assets.');
    }
  };

  // Löschen
  const handleDeleteAsset = async (id) => {
    if (!window.confirm('Wirklich löschen?')) return;
    try {
      await api.delete(`/assets/${id}`);
      setAssets(assets.filter(a => a.id !== id));
    } catch {
      alert('Fehler beim Löschen des Assets.');
    }
  };

  // Bearbeiten starten
  const handleEditClick = (a) => setEditingAsset({ ...a });

  // Bearbeitung abbrechen
  const handleCancelEdit = () => setEditingAsset(null);

  // Edit-Felder aktualisieren
  const handleEditInputChange = (e) => {
    const { name, value } = e.target;
    setEditingAsset(prev => ({ ...prev, [name]: value }));
  };

  // Speichern der Änderungen
  const handleSaveAsset = async () => {
    if (!editingAsset.itemId || !editingAsset.quantity) {
      alert('Bitte Artikel und Menge auswählen.');
      return;
    }
    try {
      const payload = {
        itemId: editingAsset.itemId,
        customDescription: editingAsset.customDescription,
        customType: editingAsset.customType,
        quantity: editingAsset.quantity,
        periodFrom: editingAsset.periodFrom,
        periodTo: editingAsset.periodTo,
        status: editingAsset.status || 'Mietanfrage',
        customerId: editingAsset.customerId,
        contactId: editingAsset.contactId,
        prospectId: editingAsset.prospectId,
        prospectContactId: editingAsset.prospectContactId,
        pipelineInstId: editingAsset.pipelineInstId,
      };
      const response = await api.put(`/assets/${editingAsset.id}`, payload);
      setAssets(assets.map(a => a.id === editingAsset.id ? response.data : a));
      setEditingAsset(null);
    } catch (err) {
      alert(err.response?.data?.error || 'Fehler beim Aktualisieren des Assets.');
    }
  };

  if (loading) return <div>Lade Assets...</div>;
  if (error) return <div>{error}</div>;

  const accountOptions = [
    ...customers.map(customer => ({
      value: `customer-${customer.id}`,
      accountId: customer.id,
      accountType: 'customer',
      label: customer.company
        || `${customer.firstName || ''} ${customer.lastName || ''}`.trim()
        || `Kunde #${customer.id}`,
    })),
    ...prospects.map(prospect => ({
      value: `prospect-${prospect.id}`,
      accountId: prospect.id,
      accountType: 'prospect',
      label: prospect.company
        || `${prospect.firstName || ''} ${prospect.lastName || ''}`.trim()
        || `Interessent I-${prospect.id}`,
    })),
  ].sort((a, b) => a.label.localeCompare(b.label, 'de'));

  const selectedFilterAccount = accountOptions.find(option => (
    (option.accountType === 'customer' && option.accountId === filter.customerId)
    || (option.accountType === 'prospect' && option.accountId === filter.prospectId)
  )) || null;

  const selectedEditAccount = editingAsset
    ? accountOptions.find(option => (
      (option.accountType === 'customer' && option.accountId === editingAsset.customerId)
      || (option.accountType === 'prospect' && option.accountId === editingAsset.prospectId)
    )) || null
    : null;

  const contactOptions = contacts.map(contact => ({
    value: contact.id,
    label: `${contact.contactFirstName || contact.firstName || ''} ${contact.contactLastName || contact.lastName || ''}`.trim()
      || `#${contact.id}`,
  }));
  const selectedEditContactId = editingAsset?.prospectId
    ? editingAsset.prospectContactId
    : editingAsset?.contactId;
  const selectedEditContact = contactOptions.find(option => option.value === selectedEditContactId) || null;
  const formatAccountOption = option => (
    option.accountType === 'prospect' ? <em>{option.label}</em> : option.label
  );
  const itemOptions = items.map(item => ({
    value: item.id,
    label: item.name,
    description: item.description || '',
  }));

  // Styles
  const buttonStyle = { fontSize: '15px', padding: '5px 10px', color: '#fff', border: 'none', borderRadius: '5px', cursor: 'pointer', marginRight: '5px' };
  const addButtonStyle = { ...buttonStyle, background: '#28a745' };
  const editButtonStyle = { ...buttonStyle, background: '#007bff' };
  const saveButtonStyle = { ...buttonStyle, background: '#28a745' };
  const cancelButtonStyle = { ...buttonStyle, background: '#6c757d' };
  const deleteButtonStyle = { ...buttonStyle, background: '#dc3545' };

  return (
    <div className="asset-form">
      <Helmet>
        <title>Bedarfe / Assets</title>
        <meta name="description" content="CRM Bedarfe verwalten" />
      </Helmet>
      <h2>Bedarfe / Assets</h2>

      {/* Formular für neuen Bedarf */}
      {showAddRow && (
  <div className="asset-add-grid">
  <textarea
    className="asset-add-description"
    rows={3}
    value={newAsset.customDescription}
    onChange={e => setNewAsset({ ...newAsset, customDescription: e.target.value })}
    placeholder="Beschreibung"
  />
 <Select
  className="asset-add-item-select"
  name="itemId"
  value={itemOptions.find(option => String(option.value) === String(newAsset.itemId)) || null}
  onChange={option =>
    setNewAsset(prev => ({ ...prev, itemId: option ? option.value : '' }))
  }
  options={itemOptions}
  filterOption={filterItemOption}
  formatOptionLabel={formatItemOption}
  menuPortalTarget={document.body}
  menuPosition="fixed"
  
  placeholder="Artikel suchen"
  
  isClearable
  styles={{
    container: base => ({ ...base, width: '100%', minWidth: 0, margin: 0 }),
    control: base => ({ ...base, minHeight: 40 }),
    menu: base => ({ ...base, zIndex: 10020 }),
    menuPortal: base => ({ ...base, zIndex: 11000 }),
  }}
/>
  <input
    className="asset-add-quantity"
    type="number"
    value={newAsset.quantity}
    onChange={e => setNewAsset({ ...newAsset, quantity: e.target.value })}
    min={1}
    placeholder="Menge"
  />
  <input
    className="asset-add-date"
    type="date"
    value={newAsset.periodFrom}
    onChange={e => setNewAsset({ ...newAsset, periodFrom: e.target.value })}
  />
  <input
    className="asset-add-date"
    type="date"
    value={newAsset.periodTo}
    onChange={e => setNewAsset({ ...newAsset, periodTo: e.target.value })}
  />
  {/* Status Dropdown nach ENUM aus dem Modell */}
  <select
    className="asset-add-status"
    value={newAsset.status}
    onChange={e => setNewAsset({ ...newAsset, status: e.target.value })}
  >
    <option value="Mietanfrage">Mietanfrage</option>
    <option value="Kaufanfrage">Kaufanfrage</option>
    <option value="angeboten">angeboten</option>
    <option value="vermietet">vermietet</option>
    <option value="verkauft">verkauft</option>
    <option value="beendet">beendet</option>
  </select>
  <button
  className="asset-add-button"
  onClick={handleAddAsset} 
   style={addButtonStyle}
>
    Hinzufügen
  </button>
</div>
)}


{showFilters && (
  <div
  style={{
    display: 'flex',
    gap: 16,
    margin: '15px 0',
    alignItems: 'center',
    justifyContent: 'center', // Neu! Zentriert horizontal
    width: '100%',            // Optional, volle Breite
  }}
>
 <Select
  placeholder="Kunde / Interessent"
  name="accountId"
  value={selectedFilterAccount}
  onChange={option => {
    setFilter(f => ({
      ...f,
      customerId: option?.accountType === 'customer' ? option.accountId : '',
      prospectId: option?.accountType === 'prospect' ? option.accountId : '',
    }));
    setCurrentPage(1);
  }}
  options={accountOptions}
  formatOptionLabel={formatAccountOption}
  isClearable
  styles={{ container: base => ({ ...base, minWidth: 210, marginTop: -15 }) }}
/>
<Select
  placeholder="Vorgang"
  name="pipelineInstId"
  value={assets
    .map(a => a.pipelineInstance)
    .filter((v, i, arr) => v && arr.findIndex(x => x.id === v.id) === i)
    .map(pi => ({ value: pi.id, label: pi.name }))
    .find(pi => pi.value === filter.pipelineInstId) || null
  }
  onChange={option => {
    setFilter(f => ({ ...f, pipelineInstId: option ? option.value : '' }));
    setCurrentPage(1);
  }}
  options={assets
    .map(a => a.pipelineInstance)
    .filter((v, i, arr) => v && arr.findIndex(x => x.id === v.id) === i)
    .map(pi => ({ value: pi.id, label: pi.name }))}
  isClearable
  styles={{ container: base => ({ ...base, minWidth: 160, marginTop: -15  }) }}
/>
<Select
  placeholder="Artikel"
  name="itemId"
  value={itemOptions.find(option => String(option.value) === String(filter.itemId)) || null}
  onChange={option => {
    setFilter(f => ({ ...f, itemId: option ? option.value : '' }));
    setCurrentPage(1);
  }}
  options={itemOptions}
  filterOption={filterItemOption}
  formatOptionLabel={formatItemOption}
  menuPortalTarget={document.body}
  menuPosition="fixed"
  isClearable
  styles={{
    container: base => ({ ...base, minWidth: 160, marginTop: -15 }),
    menuPortal: base => ({ ...base, zIndex: 11000 }),
  }}
/>
<input
  type="date"
  value={filter.dateFrom}
  onChange={e => {
    setFilter(f => ({ ...f, dateFrom: e.target.value }));
    setCurrentPage(1);
  }}
  style={{ maxWidth: "150px" }}
  placeholder="Von"
/>
<input
  type="date"
  value={filter.dateTo}
  onChange={e => {
    setFilter(f => ({ ...f, dateTo: e.target.value }));
    setCurrentPage(1);
  }}
  style={{ maxWidth: "150px" }}
  placeholder="Bis"
/>
<input
  type="text"
  value={filter.description}
  onChange={e => {
    setFilter(f => ({ ...f, description: e.target.value }));
    setCurrentPage(1);
  }}
  placeholder="Beschreibung"
  style={{ maxWidth: 250 }}
/>
  
  <button
  style={{ marginTop:'-15px', padding: '5px 12px', background: '#eee', border: '1px solid #bbb', borderRadius: 5 }}
    onClick={async () => {
      setFilter({
        customerId: '',
        prospectId: '',
        pipelineInstId: '',
        itemId: '',
        dateFrom: '',
        dateTo: '',
        description: '',
      });
      setLoading(true);
      const res = await api.get('/assets', { params: { pipelineInstId } }); // oder ohne Filter
setAssets(extractAssets(res.data));
      setLoading(false);
    }}
   
  >
    ↩️
  </button>
</div>
)}

      {/* Tabelle mit Assets */}
      <div className="asset-table-scroll">
      <table>
        <thead>
          <tr>
            <th>ID</th>
            <th>Beschreibung</th>
            <th>Vorgang</th>
            <th>Kunde / Interessent</th>
            <th>Kontakt</th>
            <th>Artikel</th>
            <th>Menge</th>
            <th>Von</th>
            <th>Bis</th>
            <th>Status</th>
            <th>Aktionen</th>
          </tr>
        </thead>
        <tbody>
          {assets.map(a => (
            <tr
              key={a.id}
              className={editingAsset?.id === a.id ? 'asset-edit-row' : undefined}
            >
              <td>{a.id}</td>
              <td>
                {editingAsset?.id === a.id ? (
                  <textarea
                    className="asset-edit-control asset-edit-description"
                    rows={3}
                    name="customDescription"
                    value={editingAsset.customDescription || ''}
                    onChange={handleEditInputChange}
                  />
                ) : (
                  <span className="asset-description-text">{a.customDescription || '-'}</span>
                )}
              </td>
              <td>
  {a.pipelineInstance
    ? a.pipelineInstance.name
    : (a.pipelineInstId || "-")}
              </td>
              <td>
                {editingAsset?.id === a.id ? (
                  <Select
                    value={selectedEditAccount}
                    onChange={handleEditAccountChange}
                    options={accountOptions}
                    formatOptionLabel={formatAccountOption}
                    placeholder="Kunde / Interessent"
                    isClearable
                    isDisabled={!!editingAsset.pipelineInstId}
                    styles={{ container: base => ({ ...base, minWidth: 200 }) }}
                  />
                ) : a.prospect ? (
                  <em>
                    {a.prospect.company
                      || `${a.prospect.firstName || ''} ${a.prospect.lastName || ''}`.trim()
                      || '-'}
                  </em>
                ) : a.customer ? (
                  a.customer.company
                    || `${a.customer.firstName || ''} ${a.customer.lastName || ''}`.trim()
                    || '-'
                ) : '-'}
              </td>
              <td>
                {editingAsset?.id === a.id ? (
                  <Select
                    value={selectedEditContact}
                    onChange={handleEditContactChange}
                    options={contactOptions}
                    placeholder="Ansprechpartner"
                    isClearable
                    styles={{ container: base => ({ ...base, minWidth: 170 }) }}
                  />
                ) : (a.contact || a.prospectContact)
                  ? `${(a.contact || a.prospectContact).contactFirstName || (a.contact || a.prospectContact).firstName || ''} ${(a.contact || a.prospectContact).contactLastName || (a.contact || a.prospectContact).lastName || ''}`.trim()
                  : '-'}
              </td>
              <td>
  {editingAsset?.id === a.id ? (
    <Select
      name="itemId"
      value={itemOptions.find(option => String(option.value) === String(editingAsset.itemId)) || null}
      onChange={option =>
        setEditingAsset(prev => ({ ...prev, itemId: option ? option.value : '' }))
      }
      options={itemOptions}
      filterOption={filterItemOption}
      formatOptionLabel={formatItemOption}
      menuPortalTarget={document.body}
      menuPosition="fixed"
      placeholder="Artikel suchen"
      isClearable
      styles={{
        container: base => ({ ...base, minWidth: 170 }),
        menuPortal: base => ({ ...base, zIndex: 11000 }),
      }}
    />
  ) : (a.item ? a.item.name : items.find(i => i.id === a.itemId)?.name || '-')}
</td>
              <td>
                {editingAsset?.id === a.id ? (
                  <input
                    className="asset-edit-control asset-edit-quantity"
                    type="number"
                    name="quantity"
                    value={editingAsset.quantity}
                    onChange={handleEditInputChange}
                  />
                ) : a.quantity}
              </td>
             <td>
  {editingAsset?.id === a.id ? (
    <input
      className="asset-edit-control asset-edit-date"
      type="date"
      name="periodFrom"
      value={editingAsset.periodFrom || ''}
      onChange={handleEditInputChange}
    />
  ) : formatDate(a.periodFrom)}
</td>
<td>
  {editingAsset?.id === a.id ? (
    <input
      className="asset-edit-control asset-edit-date"
      type="date"
      name="periodTo"
      value={editingAsset.periodTo || ''}
      onChange={handleEditInputChange}
    />
  ) : formatDate(a.periodTo)}
</td>
             <td>
  {editingAsset?.id === a.id ? (
    <select
      className="asset-edit-control asset-edit-status"
      name="status"
      value={editingAsset.status}
      onChange={handleEditInputChange}
    >
      <option value="Mietanfrage">Mietanfrage</option>
      <option value="Kaufanfrage">Kaufanfrage</option>
      <option value="angeboten">angeboten</option>
      <option value="vermietet">vermietet</option>
      <option value="verkauft">verkauft</option>
      <option value="beendet">beendet</option>
    </select>
  ) : (a.status || '-')}
</td>
              <td>
                {editingAsset?.id === a.id ? (
                  <div className="asset-row-actions">
                    <button onClick={handleSaveAsset} style={saveButtonStyle}>Speichern</button>
                    <button onClick={handleCancelEdit} style={cancelButtonStyle}>Abbrechen</button>
                  </div>
                ) : (
                  <div className="asset-row-actions">
                    <button onClick={() => handleEditClick(a)} style={editButtonStyle}>Bearbeiten</button>
                    <button onClick={() => handleDeleteAsset(a.id)} style={deleteButtonStyle}>Löschen</button>
                  </div>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      </div>
<div style={{ marginTop: '1rem', textAlign: 'center' }}>
  <button
    onClick={() => setCurrentPage(page => Math.max(1, page - 1))}
    disabled={currentPage === 1}
    style={{ fontSize: '15px', padding: '8px 12px', backgroundColor: 'rgb(155, 155, 155)', border: 'none', borderRadius: '5px', cursor: 'pointer' }}
  >
    Vorherige
  </button>
  <span style={{ margin: '0 1rem' }}>
    Seite {currentPage} von {totalPages}
  </span>
  <button
    onClick={() => setCurrentPage(page => Math.min(totalPages, page + 1))}
    disabled={currentPage >= totalPages}
    style={{ fontSize: '15px', padding: '8px 12px', backgroundColor: 'rgb(155, 155, 155)', border: 'none', borderRadius: '5px', cursor: 'pointer' }}
  >
    Nächste
  </button>
</div>


    </div>
  );
};

export default AssetForm;
